import React, { useState, useEffect, useRef } from "react";
import Typography from "@/app/deb/components/typography/Typography";
import clsx from "clsx";
import { usePrevious } from "@/app/deb/services/usePrevious";

// Animation configuration
const CHAR_ANIMATION_DELAY_MS = 12;
const WORD_ANIMATION_DELAY_MS = 100;
const SENTENCE_ANIMATION_DELAY_MS = 300;
const CURSOR_SYMBOL = "▌"; // Block cursor symbol

type AnimationMode = "none" | "animateCharacters" | "animateWords";

interface TextBubbleProps {
  displayText: string;
  typographyClassName?: string;
  className?: string;
  animationMode?: AnimationMode;
  animateFirstText?: boolean;
  maxBubbleHeight?: number;
}

const TextBubble: React.FC<TextBubbleProps> = ({
  displayText,
  className,
  typographyClassName,
  animationMode = "animateCharacters",
  animateFirstText = true,
  maxBubbleHeight = 100,
}) => {
  const [visibleText, setVisibleText] = useState<string>("");
  const [isAnimating, setIsAnimating] = useState<boolean>(false);
  const [bubbleHeight, setBubbleHeight] = useState<number | undefined>(undefined); // State for bubble height
  const contentRef = useRef<HTMLDivElement | null>(null); // Ref for measuring content height
  const prevDisplayText = usePrevious<string>(displayText);
  const animationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isFirstRender = useRef<boolean>(true);
  const isMountedRef = useRef<boolean>(true);

  // Clean up any running animations
  const cleanupAnimation = () => {
    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current);
      animationTimeoutRef.current = null;
    }
  };

  // Start animation function
  const startAnimation = (text: string) => {
    cleanupAnimation();

    if (text.length === 0) {
      setVisibleText("");
      setIsAnimating(false);
      return;
    }

    if (animationMode === "animateCharacters") {
      setVisibleText(""); // Wipe previous text for animation
      setIsAnimating(true);
      let currentIndex = 0;
      const animateNextChar = () => {
        if (!isMountedRef.current) return;

        if (currentIndex <= text.length) {
          setVisibleText(text.substring(0, currentIndex) + (currentIndex < text.length ? CURSOR_SYMBOL : ""));
          currentIndex++;

          const delay =
            currentIndex > 1 &&
            (text[currentIndex - 2] === "." || text[currentIndex - 2] === "?" || text[currentIndex - 2] === "!")
              ? SENTENCE_ANIMATION_DELAY_MS
              : CHAR_ANIMATION_DELAY_MS;

          animationTimeoutRef.current = setTimeout(animateNextChar, delay);
        } else {
          setIsAnimating(false);
        }
      };
      animateNextChar();
    } else if (animationMode === "animateWords") {
      setVisibleText(""); // Wipe previous text for animation
      setIsAnimating(true);
      const words = text.split(/\s+/).filter((word) => word.length > 0);
      let currentWordIndex = 0;
      const animateNextWord = () => {
        if (!isMountedRef.current) return;

        if (currentWordIndex <= words.length) {
          const currentWords = words.slice(0, currentWordIndex);
          const visibleWordsString = currentWords.join(" ");

          setVisibleText(
            visibleWordsString +
              (currentWordIndex < words.length ? (currentWords.length > 0 ? " " : "") + CURSOR_SYMBOL : ""),
          );

          const lastWord = currentWordIndex > 0 ? words[currentWordIndex - 1] || "" : "";
          const endsWithPunctuation = lastWord.endsWith(".") || lastWord.endsWith("?") || lastWord.endsWith("!");

          currentWordIndex++;

          animationTimeoutRef.current = setTimeout(
            animateNextWord,
            endsWithPunctuation ? SENTENCE_ANIMATION_DELAY_MS : WORD_ANIMATION_DELAY_MS,
          );
        } else {
          setIsAnimating(false);
        }
      };
      animateNextWord();
    } else {
      // animationMode === "none"
      setVisibleText(text);
      setIsAnimating(false);
    }
  };

  // Effect for initial render and unmount cleanup
  useEffect(() => {
    isMountedRef.current = true;
    if (isFirstRender.current) {
      if (animationMode !== "none" && animateFirstText) {
        startAnimation(displayText);
      } else {
        setVisibleText(displayText);
        setIsAnimating(false);
      }
      isFirstRender.current = false;
    }

    return () => {
      isMountedRef.current = false;
      cleanupAnimation();
    };
  }, []); // Empty dependency array to run only on mount and unmount

  // Effect for handling displayText changes
  useEffect(() => {
    if (!isFirstRender.current) {
      // Skip if it's the first render
      if (displayText !== prevDisplayText || (prevDisplayText === undefined && displayText !== "")) {
        if (animationMode !== "none") {
          startAnimation(displayText);
        } else {
          cleanupAnimation(); // Ensure cursor/animation stops
          setVisibleText(displayText);
          setIsAnimating(false);
        }
      } else if (animationMode === "none" && visibleText !== displayText) {
        // If animationMode is switched to 'none' (and text hasn't changed), update text immediately
        cleanupAnimation(); // Ensure cursor/animation stops
        setVisibleText(displayText);
        setIsAnimating(false);
      }
    }
  }, [displayText, animationMode, animateFirstText, prevDisplayText]);

  // Effect for handling animationMode or animateFirstText changes after initial render
  useEffect(() => {
    if (!isFirstRender.current) {
      // If animationMode changes, restart animation with current text
      // or set text directly if mode is 'none' or animateFirstText is false
      if (animationMode !== "none" && animateFirstText) {
        startAnimation(displayText);
      } else {
        cleanupAnimation();
        setVisibleText(displayText);
        setIsAnimating(false);
      }
    }
  }, [animationMode, animateFirstText, displayText]);

  // Effect to update bubble height dynamically
  useEffect(() => {
    if (contentRef.current) {
      const contentHeight = contentRef.current.offsetHeight;
      const padding = 16 * 2; // Assuming 16px padding on top and bottom
      const buffer = 2; // Add a small buffer to prevent scrollbar
      setBubbleHeight(Math.min(contentHeight + padding + buffer, maxBubbleHeight)); // Add padding and buffer to the height
    }
  }, [visibleText]); // Update height whenever visibleText changes

  return (
    <div
      className={clsx(
        `bubblediv bg-base-100 w-full p-4 border-[1px] border-base-content rounded-lg scrollbar-thin overflow-y-auto z-0 relative`,
        className,
      )}
      style={{
        height: bubbleHeight !== undefined ? `${bubbleHeight}px` : "auto", // Apply dynamic height
        opacity: displayText && displayText.length > 0 ? 1 : 0, // Apply dynamic opacity
        transform: displayText && displayText.length > 0 ? "scale(1)" : "scale(0.25)", // Scale animation
        transformOrigin: "bottom left", // Anchor scale animation to bottom-left
        transition: "height 0.25s ease-in-out, opacity 0.25s ease-in-out, transform 0.3s ease-in-out", // Smooth height, opacity, and scale transitions
      }}
    >
      <div
        ref={contentRef}
        className="w-full h-auto"
      >
        <Typography className={clsx("w-full h-full", typographyClassName)}>{visibleText}</Typography>
      </div>
    </div>
  );
};

export default TextBubble;
